import React, { useCallback, useMemo, useState } from 'react';
import { AppButton, Box, FlexControlledForm, InfoOutlineIcon, Inscription, Preloader } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';

import { ModalWrapper } from '@/components';
import theme from '@/styles/theme';
import { getTaxYears } from '@/utils/handlers';

import { TaxType } from '../Contributions/contributions.types';

import { areAmountsValidBySum } from './common';
import { ChangedContributionRecord } from './useChangedContributionRecords.store';
import { useGetEditFormFields } from './useGetEditFormFields';

const taxYears = getTaxYears();

export interface FormValues {
  employeeFullName?: string;
  planCode?: string;
  employeeContributionAmount?: string;
  employerContributionAmount?: string;
  contributionDateOn?: string;
  taxYear?: string;
  taxType?: TaxType;
  memo?: string;
}

interface Props {
  isVisible: boolean;
  close: () => void;
  onSave?: (data?: FormValues) => void;
  record?: ChangedContributionRecord;
  hasRecordAmountsValidErrorBySum?: boolean;
  isLoading?: boolean;
}

export const EditDataModalPopup: React.FC<Props> = ({ isLoading, isVisible, close, onSave, record, hasRecordAmountsValidErrorBySum }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>();
  const isAllRecordsUpdatingMode = useMemo(() => !record, [record]);
  const hasAmountError = useMemo(() => {
    if (isAllRecordsUpdatingMode && hasRecordAmountsValidErrorBySum
      && !areAmountsValidBySum(formValues?.employeeContributionAmount, formValues?.employerContributionAmount)) {
      return true;
    }
    if (
      isAllRecordsUpdatingMode &&
      (!formValues?.employeeContributionAmount || !formValues?.employerContributionAmount)
    ) {
      return false;
    }

    return !areAmountsValidBySum(formValues?.employeeContributionAmount, formValues?.employerContributionAmount);
  }, [formValues, isAllRecordsUpdatingMode, hasRecordAmountsValidErrorBySum]);
  const hasDateError = useMemo(
    () => !isAllRecordsUpdatingMode && !formValues?.contributionDateOn,
    [formValues, isAllRecordsUpdatingMode]
  );
  const isDirty = useMemo(() => {
    if (isAllRecordsUpdatingMode) {
      return (
        formValues?.employeeContributionAmount ||
        formValues?.employerContributionAmount ||
        formValues?.contributionDateOn ||
        formValues?.taxYear ||
        formValues?.taxType ||
        formValues?.memo
      );
    }

    const isDateChanged =
      formValues?.contributionDateOn && record?.contributionDateOn
        ? !dayjs(record?.contributionDateOn).isSame(formValues?.contributionDateOn, 'day')
        : formValues?.contributionDateOn !== record?.contributionDateOn;

    return (
      toNumber(record?.employeeContributionAmount) !== toNumber(formValues?.employeeContributionAmount) ||
      toNumber(record?.employerContributionAmount) !== toNumber(formValues?.employerContributionAmount) ||
      isDateChanged ||
      record?.taxYear !== formValues?.taxYear ||
      record?.taxType !== formValues?.taxType ||
      record?.memo !== formValues?.memo
    );
  }, [formValues, record, isAllRecordsUpdatingMode]);
  const fields = useGetEditFormFields({
    record,
    taxYears,
    areErrorsShown: isSubmitted,
    hasAmountError,
    hasDateError,
    isLoading,
  });

  const onSubmit = useCallback(() => {
    setIsSubmitted(true);

    if (isAllRecordsUpdatingMode && hasAmountError) return;
    if (onSave && !hasAmountError) onSave(formValues);
    close();
  }, [formValues, close, onSave, setIsSubmitted, hasAmountError, isAllRecordsUpdatingMode]);

  return (
    <ModalWrapper
      testId={isAllRecordsUpdatingMode ? 'update_all_records' : 'edit_one_record'}
      visible={isVisible}
      title={isAllRecordsUpdatingMode ? 'Update All Records' : 'Edit Record'}
      onSetVisible={close}
      titleBoxStyle={{ paddingBottom: theme.spacings.spacing12 }}
    >
      {isAllRecordsUpdatingMode ? (
        <Box align="center" pad={{ bottom: 'spacing12' }}>
          <Inscription lineHeight="20px" color="textTitle">
            Identify values to be applied to all records.
          </Inscription>
        </Box>
      ) : null}

      <Box background="module" pad="spacing24" round="moduleRound">
        <Box
          background="canvas"
          round="container1Round"
          border={{ color: 'border2', size: 'small' }}
          pad={{ horizontal: 'spacing24', vertical: 'spacing32' }}
          elevation="default"
        >
          <FlexControlledForm
            testId="edit_records"
            editMode
            fields={fields}
            isThinMode
            onChangeValues={setFormValues}
            wrapperStyle={{ border: 'none' }}
            formStyle={{ margin: 0 }}
          />
        </Box>

        {isAllRecordsUpdatingMode ? (
          <Box
            data-testId="info_banner"
            margin={{ top: 'spacing24' }}
            direction="row"
            justify="end"
            background="canvas"
            round="container1Round"
          >
            <Box
              border={{ color: 'border2', size: 'xsmall' }}
              align="center"
              round="container1Round"
              pad="spacing12"
              style={{ position: 'relative' }}
            >
              <Box
                round="2px"
                height="2px"
                background="info"
                width="calc(100% - 24px)"
                style={{ top: '-1px', position: 'absolute' }}
              />
              <Box
                border={{ color: 'infoBorder', size: 'small' }}
                round="8px"
                background="infoContainer"
                pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
              >
                <Box direction="row" align="center">
                  <Box margin={{ right: 'spacing8' }} width={{ min: '24px' }}>
                    <InfoOutlineIcon color="info" />
                  </Box>
                  <Inscription color="textBody" weight="bold" lineHeight="141%">
                    Here you can apply details to all contribution record without having to update each record
                    individually.
                  </Inscription>
                </Box>
                <Box margin={{ top: 'spacing8' }}>
                  <Inscription color="textBody" lineHeight="20px">
                    You can change any of these values for specific employees and accounts in the next step.
                  </Inscription>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>

      <Box margin={{ top: 'spacing24' }} direction="row" justify="end">
        <Box margin={{ right: 'spacing12' }}>
          <AppButton testId="EditDataModalPopup_Cancel" width="160px" buttonType="secondary" onClick={close}>
            Cancel
          </AppButton>
        </Box>
        <AppButton
          testId="EditDataModalPopup_Save"
          width="160px"
          buttonType="primary"
          onClick={onSubmit}
          disabled={!isDirty || (isAllRecordsUpdatingMode && isSubmitted && hasAmountError) || isLoading}
        >
          {isLoading ? (
            <Preloader testId="updating_loading" />
          ) : 'Save'}
        </AppButton>
      </Box>
    </ModalWrapper>
  );
};
